import { ErrorMessage, Spinner } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSoftProgramByIdQuery } from "@/hooks/api/softProgram.queries";
import {
  ArrowLeft,
  Book,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  User,
  Users,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const SoftProgramDetailPlaceholder = () => {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
};

const ActivitySoftSkillsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: program,
    isLoading,
    isError,
  } = useSoftProgramByIdQuery(id || "");

  if (isLoading) return <SoftProgramDetailPlaceholder />;
  if (isError || !program) {
    return (
      <ErrorMessage
        message="Failed to load soft skills program details."
        onRetry={() => navigate("/activities/soft-skills")}
      />
    );
  }

  // Derived mock data to match UI
  const enrolledCount = program.students.length;
  // Placeholder metrics
  const completedCount = Math.floor(enrolledCount * 0.84);
  const inProgressCount = enrolledCount - completedCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/activities/soft-skills")}
            className="mt-1 h-8 w-8 rounded-full border border-gray-200"
          >
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{program.name}</h1>
            <p className="text-gray-500">
              Vendor ID: {program.vendor}{" "}
              {/* Mapped as subtitle to match LeadershipEdge Institute */}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge
            variant={program.status === "completed" ? "default" : "secondary"}
            className="bg-black px-3 py-1 text-xs uppercase text-white hover:bg-black/90"
          >
            {program.status}
          </Badge>
          <Badge
            variant="outline"
            className="border-gray-200 bg-gray-100 px-3 py-1 text-xs text-gray-900"
          >
            {program.mode}
          </Badge>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Users size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Enrolled</p>
              <p className="text-lg font-bold text-blue-600">{enrolledCount}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600">
              <CheckCircle2 size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Completed</p>
              <p className="text-lg font-bold text-green-600">
                {completedCount}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-orange-600">
              {/* Using a trend arrow-like icon placeholder */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-trending-up"
              >
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                <polyline points="16 7 22 7 22 13" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">In Progress</p>
              <p className="text-lg font-bold text-orange-600">
                {inProgressCount}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 text-purple-600">
              <Clock size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="font-semibold text-gray-900">{program.duration}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Vendor Information */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <Building2 size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Vendor Information
                </h3>
              </div>
              <CardContent className="space-y-6 px-6 pb-6">
                <div>
                  <p className="mb-1 text-sm text-gray-500">Vendor</p>
                  <p className="font-semibold text-gray-900">
                    Vendor ID: {program.vendor}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-sm text-gray-500">Address</p>
                  <p className="text-sm leading-relaxed text-gray-900">
                    123 Training Plaza, New York, NY 10001
                  </p>
                </div>
                <hr className="border-gray-50" />
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-blue-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                    <a
                      href={`mailto:contact@vendor${program.vendor}.com`}
                      className="hover:underline"
                    >
                      contact@vendor.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <span>+1-555-123-4567</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Schedule */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <Calendar size={18} className="font-bold text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">Schedule</h3>
              </div>
              <CardContent className="space-y-6 px-6 pb-6">
                <div>
                  <p className="mb-1 text-sm text-gray-500">Duration</p>
                  <p className="font-bold text-gray-900">{program.duration}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Start Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(program.startDate).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">End Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(program.endDate).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                </div>

                <hr className="border-gray-50" />

                <div>
                  <p className="mb-1 text-xs text-gray-500">Timing</p>
                  <p className="text-sm text-gray-900">Monday - Friday</p>
                  <p className="text-sm font-medium text-gray-900">
                    6:00 PM - 8:00 PM
                  </p>
                </div>

                <div className="pt-2">
                  <div className="mb-2 flex items-end justify-between">
                    <span className="text-xs text-gray-500">Progress</span>
                    <span className="text-sm font-bold text-gray-900">
                      6/10 sessions
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: "60%" }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trainer & Coordinator */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <Users size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Trainer & Coordinator
                </h3>
              </div>
              <CardContent className="space-y-6 px-6 pb-6">
                <div>
                  <h4 className="mb-1 text-sm text-gray-500">Trainer</h4>
                  <p className="font-bold text-gray-900">Dr. Michael Roberts</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Leadership & Organizational Development
                  </p>
                  <p className="mt-1 text-xs text-gray-400">15+ years</p>
                </div>
                <hr className="border-gray-50" />
                <div>
                  <h4 className="mb-1 text-sm text-gray-500">
                    College Coordinator
                  </h4>
                  <p className="font-bold text-gray-900">
                    Prof. Sarah Mitchell
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Training & Placement
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Program Details */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <FileText size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Program Details
                </h3>
              </div>
              <CardContent className="space-y-6 p-6">
                <div>
                  <h4 className="mb-3 font-bold text-gray-900">Description</h4>
                  <p className="text-sm leading-relaxed text-gray-500">
                    {program.programDescription}
                  </p>
                </div>
                <div>
                  <h4 className="mb-3 font-bold text-gray-900">
                    Learning Objectives
                  </h4>
                  <ul className="ml-1 list-inside list-disc space-y-2 text-sm text-gray-500">
                    {program.programObjectives
                      .split("\n")
                      .map((objective, i) => (
                        <li key={i}>{objective}</li>
                      ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Topics & Assessment */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <Book size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Topics & Assessment
                </h3>
              </div>
              <CardContent className="space-y-6 p-6">
                <div>
                  <h4 className="mb-3 font-bold text-gray-900">
                    Topics Covered
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Leadership Fundamentals",
                      "Team Dynamics and Management",
                      "Effective Decision Making",
                      "Conflict Resolution",
                      "Strategic Thinking",
                      "Emotional Intelligence",
                      "Change Management",
                    ].map((topic) => (
                      <Badge
                        key={topic}
                        variant="secondary"
                        className="border-gray-200/50 bg-gray-100/80 text-gray-700 hover:bg-gray-200"
                      >
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>

                <hr className="border-gray-50" />

                <div>
                  <h4 className="mb-3 font-bold text-gray-900">
                    Assessment Methods
                  </h4>
                  <ul className="ml-1 list-inside list-disc space-y-2 text-sm text-gray-500">
                    <li>Group Activities and Presentations</li>
                    <li>Case Study Analysis</li>
                    <li>Leadership Simulation Exercises</li>
                    <li>Peer Feedback Sessions</li>
                    <li>Final Project Presentation</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enrolled Students */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Users size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Enrolled Students ({program.students.length})
              </h3>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {program.students.map((studentId, idx) => (
                  <div
                    key={studentId}
                    className="flex flex-col justify-between rounded-2xl border border-gray-100 p-6"
                  >
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 font-bold text-blue-600">
                            <User size={20} />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">
                              Student {studentId}
                            </h4>
                            <div className="mt-0.5 text-[10px] text-gray-500">
                              CSE202100{idx + 1}
                            </div>
                            <div className="mt-1 flex">
                              <Badge
                                variant="outline"
                                className="border-gray-200 px-1.5 py-0 text-[10px] text-gray-500"
                              >
                                CSE
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Badge className="whitespace-nowrap rounded-full bg-black px-3 py-1 text-[10px] uppercase text-white">
                          {idx === 0 ? "In Progress" : "In Progress"}
                        </Badge>
                      </div>

                      {/* Stats */}
                      <div className="space-y-2 text-xs text-gray-500">
                        <div className="flex justify-between">
                          <span>Attendance</span>
                          <span className="font-bold text-gray-900">
                            {90 + idx * 2}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Assignments</span>
                          <span className="font-bold text-gray-900">
                            {8 + (idx % 3)}/10
                          </span>
                        </div>
                      </div>

                      <div className="rounded-xl border border-blue-100/50 bg-blue-50/50 p-3">
                        <p className="mb-1 text-xs text-gray-500">Score</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {85 + idx * 3}
                        </p>
                      </div>

                      <div className="rounded-xl border border-orange-100/50 bg-orange-50/50 p-3 text-xs text-yellow-700">
                        Active participant, showing good potential
                      </div>
                    </div>

                    <div className="mt-4 border-t border-gray-50 pt-4">
                      <Button
                        variant="outline"
                        className="h-9 w-full rounded-xl border-gray-200 text-xs text-gray-700 shadow-sm"
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Footer Grids: Tech Skills, Internships, Placements, Outcomes */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <Book size={16} className="text-gray-700" />
                <h3 className="font-bold text-gray-900">Related Tech Skills</h3>
              </div>
              <CardContent className="space-y-3 px-6 pb-6">
                <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3">
                  <p className="mb-1 text-xs font-semibold text-gray-900">
                    Full Stack Web Development
                  </p>
                  <p className="text-[10px] text-gray-500">Completed</p>
                </div>
              </CardContent>
            </Card>

            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-target text-gray-700"
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
                <h3 className="font-bold text-gray-900">Related Internships</h3>
              </div>
              <CardContent className="space-y-3 px-6 pb-6">
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3">
                  <p className="mb-1 text-xs font-semibold text-gray-900">
                    Software Development Intern
                  </p>
                  <p className="text-[10px] text-gray-500">Google Inc.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-medal text-gray-700"
                >
                  <path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15" />
                  <path d="M11 12 5.12 2.2" />
                  <path d="m13 12 5.88-9.8" />
                  <path d="M8 7h8" />
                  <circle cx="12" cy="17" r="5" />
                  <polyline points="12 18 10.9 15.2 8 15 10.2 13 9.6 10.1 12 11.5 14.4 10.1 13.8 13 16 15 13.1 15.2 12 18" />
                </svg>
                <h3 className="font-bold text-gray-900">Student Placements</h3>
              </div>
              <CardContent className="space-y-3 px-6 pb-6">
                <div className="rounded-xl border border-green-100 bg-green-50/50 p-3">
                  <p className="mb-1 text-xs font-semibold text-gray-900">
                    Student Option
                  </p>
                  <p className="text-[10px] text-gray-500">Amazon - SDE-I</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-target text-gray-700"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
              <h3 className="text-lg font-bold text-gray-900">
                Expected Outcomes
              </h3>
            </div>
            <CardContent className="px-6 pb-6 pt-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <CheckCircle2 size={16} className="text-green-500" />
                  <span className="text-sm text-gray-700">
                    Improved team collaboration
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <CheckCircle2 size={16} className="text-green-500" />
                  <span className="text-sm text-gray-700">
                    Enhanced decision-making skills
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <CheckCircle2 size={16} className="text-green-500" />
                  <span className="text-sm text-gray-700">
                    Better conflict resolution
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <CheckCircle2 size={16} className="text-green-500" />
                  <span className="text-sm text-gray-700">
                    Increased confidence in leadership roles
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ActivitySoftSkillsView;
